import { ChevronDown, CircleCheck, Clock, LoaderCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useStoreStatus } from "../../hooks/useStoreStatus";
import { lookupCep } from "../../services/cep";
import { apiErrorMessage, createOrder } from "../../services/orders";
import type { AppliedCoupon, CheckoutForm, Order, PaymentMethod, SuccessLocationState } from "../../types";
import { cn, formatPrice } from "../../utils/format";
import { checkStoreStatus } from "../../utils/storeStatus";
import { Button } from "../ui/Button";
import { Modal, ModalCloseButton } from "../ui/Modal";
import { CouponField } from "./CouponField";

const EMPTY_FORM: CheckoutForm = {
  nome: "",
  whatsapp: "",
  cep: "",
  endereco: "",
  bairro: "",
  numero: "",
  complemento: "",
  pagamento: "",
  observacao: "",
  troco: "",
};

const PAYMENT_METHODS: PaymentMethod[] = ["Pix", "Cartão", "Dinheiro"];

const round2 = (value: number) => Math.round(value * 100) / 100;

const Field = ({ label, className, children }: { label: string; className?: string; children: ReactNode }) => (
  <label className={cn("block", className)}>
    <span className="mb-1.5 block text-xs font-medium text-muted">{label}</span>
    {children}
  </label>
);

export const CheckoutModal = () => {
  const navigate = useNavigate();
  const { items, count, subtotal, clearCart, isCheckoutOpen, closeCheckout } = useCart();
  const storeStatus = useStoreStatus();
  const [cliente, setCliente] = useState<CheckoutForm>(EMPTY_FORM);
  const [coupon, setCoupon] = useState<AppliedCoupon | null>(null);
  const [sending, setSending] = useState(false);

  // Estimativa para o cliente; o backend recalcula com os preços do banco.
  const discount = coupon ? round2((subtotal * coupon.percent) / 100) : 0;
  const total = round2(subtotal - discount);

  const update =
    (field: keyof CheckoutForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { value } = e.target;
      setCliente((prev) => ({ ...prev, [field]: value }));
    };

  const updateWhatsapp = (e: ChangeEvent<HTMLInputElement>) => {
    update("whatsapp")(e);
    // O cupom de primeiro pedido vale para o número validado.
    if (coupon) {
      setCoupon(null);
      toast("Cupom removido: aplique de novo com o novo WhatsApp.");
    }
  };

  const handleCEP = async (cep: string) => {
    const valor = cep.replace(/\D/g, "");
    setCliente((prev) => ({ ...prev, cep: valor }));
    if (valor.length === 8) {
      try {
        const data = await lookupCep(valor);
        if (data.erro) {
          toast.error("CEP inválido.");
          return;
        }

        setCliente((prev) => ({
          ...prev,
          endereco: data.logradouro ?? "",
          bairro: data.bairro ?? "",
        }));
      } catch {
        toast.error("Erro ao buscar CEP.");
      }
    }
  };

  const finalizarPedido = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Seu carrinho está vazio!");
      return;
    }
    if (!checkStoreStatus().isOpen) {
      toast.error("A loja está fechada no momento.");
      return;
    }
    if (!cliente.pagamento) {
      toast.error("Escolha a forma de pagamento.");
      return;
    }
    setSending(true);

    const payload: Order = {
      type: "website",
      customer_name: cliente.nome,
      customer_whatsapp: cliente.whatsapp,
      total_price: total,
      cep: cliente.cep,
      address_street: cliente.endereco,
      address_number: cliente.numero,
      address_complement: cliente.complemento,
      address_neighborhood: cliente.bairro,
      payment_method: cliente.pagamento,
      change_details: cliente.troco,
      notes: cliente.observacao || "Sem observações",
      items: items.map((i) => ({
        product_id: i.id,
        quantity: i.quantity,
        price: i.unitPrice,
        // Só os ids: o backend busca nomes e preços no banco e recalcula tudo.
        ...(i.customization && {
          notes: i.customization.notes || undefined,
          removed_ingredients: i.customization.removed.map((r) => r.id),
          extras: i.customization.extras.map((e) => ({ ingredient_id: e.id, quantity: e.quantity })),
        }),
      })),
      coupon_code: coupon?.code,
    };

    try {
      const response = await createOrder(payload);
      toast.success("Pedido enviado com sucesso!");
      clearCart();
      setCoupon(null);
      closeCheckout();
      const state: SuccessLocationState = {
        orderId: response.orderId,
        total: response.total_price,
        discount: response.discount,
      };
      navigate("/sucesso", { state });
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao processar pedido."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={isCheckoutOpen} onClose={closeCheckout} labelledBy="checkout-title">
      <div className="sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-line bg-coal/95 px-6 py-5 backdrop-blur">
        <div>
          <h2 id="checkout-title" className="flex items-center gap-2 font-display text-3xl tracking-wide">
            <CircleCheck className="size-6 text-primary" aria-hidden="true" /> Finalizar Pedido
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            {count} {count === 1 ? "item" : "itens"} · {formatPrice(total)}
          </p>
        </div>
        <ModalCloseButton onClick={closeCheckout} />
      </div>

      <form onSubmit={finalizarPedido} className="space-y-4 px-6 py-6">
        {!storeStatus.isOpen && (
          <div role="alert" className="flex gap-3 rounded-2xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger">
            <Clock className="mt-0.5 size-5 shrink-0" aria-hidden="true" />
            <p>
              <strong>{storeStatus.message}.</strong> Pedidos online só no horário de funcionamento.
            </p>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Nome">
            <input className="field" placeholder="Seu nome" autoComplete="name" required value={cliente.nome} onChange={update("nome")} />
          </Field>
          <Field label="WhatsApp">
            <input
              className="field"
              placeholder="(11) 99999-9999"
              type="tel"
              autoComplete="tel"
              required
              value={cliente.whatsapp}
              onChange={updateWhatsapp}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="CEP">
            <input
              className="field"
              placeholder="Somente números"
              inputMode="numeric"
              autoComplete="postal-code"
              required
              maxLength={8}
              value={cliente.cep}
              onChange={(e) => handleCEP(e.target.value)}
            />
          </Field>
          <Field label="Bairro">
            <input className="field" placeholder="Preenchido pelo CEP" value={cliente.bairro} readOnly />
          </Field>
        </div>

        <Field label="Endereço">
          <input className="field" placeholder="Preenchido pelo CEP" value={cliente.endereco} readOnly />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Número">
            <input className="field" placeholder="Nº" required value={cliente.numero} onChange={update("numero")} />
          </Field>
          <Field label="Complemento">
            <input className="field" placeholder="Compl." value={cliente.complemento} onChange={update("complemento")} />
          </Field>
        </div>

        <Field label="Observações">
          <textarea
            className="field h-24 resize-none py-3"
            placeholder="Ex: Tirar cebola, ponto da carne..."
            value={cliente.observacao}
            onChange={update("observacao")}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Forma de pagamento">
            <span className="relative block">
              <select className="field appearance-none pr-10" required value={cliente.pagamento} onChange={update("pagamento")}>
                <option value="">Selecione</option>
                {PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>
                    {method}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            </span>
          </Field>
          <AnimatePresence>
            {cliente.pagamento === "Dinheiro" && (
              <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}>
                <Field label="Troco">
                  <input className="field" placeholder="Troco para quanto?" value={cliente.troco} onChange={update("troco")} />
                </Field>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <CouponField
          whatsapp={cliente.whatsapp}
          applied={coupon}
          onApply={setCoupon}
          onRemove={() => setCoupon(null)}
        />

        <dl className="space-y-1.5 rounded-2xl border border-line bg-night p-4 text-sm">
          <div className="flex justify-between text-muted">
            <dt>Subtotal</dt>
            <dd>{formatPrice(subtotal)}</dd>
          </div>
          {coupon && (
            <div className="flex justify-between text-success">
              <dt>
                Desconto ({coupon.code} · {coupon.percent}%)
              </dt>
              <dd>− {formatPrice(discount)}</dd>
            </div>
          )}
          <div className="flex justify-between text-muted">
            <dt>Entrega</dt>
            <dd className="font-semibold text-success">Grátis</dd>
          </div>
          <div className="flex justify-between pt-1.5 text-base font-bold">
            <dt>Total</dt>
            <dd className="text-primary">{formatPrice(total)}</dd>
          </div>
        </dl>

        <Button type="submit" size="lg" className="w-full" disabled={sending || !storeStatus.isOpen}>
          {sending ? (
            <>
              <LoaderCircle className="size-5 animate-spin" aria-hidden="true" />
              <span className="sr-only">Enviando pedido…</span>
            </>
          ) : storeStatus.isOpen ? (
            `Confirmar pedido — ${formatPrice(total)}`
          ) : (
            "Loja fechada"
          )}
        </Button>
      </form>
    </Modal>
  );
};
